import {toValue} from 'vue';
import {onClickOutside as vueUseOnClickOutside, type MaybeElementRef, type OnClickOutsideHandler, type OnClickOutsideOptions} from '@vueuse/core';

type OnClickOutsideControlsReturn = ReturnType<typeof vueUseOnClickOutside>;

const IGNORED_WHITELIST = ['.malt-datepicker_floating'];

/**
 * Drop-in replacement of VueUse `onClickOutside` (same signature, options and return types)
 * that also ignores the app-wide whitelist of floating elements.
 *
 * @see https://vueuse.org/core/onClickOutside/
 *
 * @usage
 *
 * ```ts
 * const myRef = useTemplateRef('myRef');
 * onClickOutside(myRef, () => {
 *    // Do stuff
 * });
 *
 * const stop = onClickOutside(myRef, handler, {ignore: [otherRef], detectIframe: true});
 * const {stop, cancel, trigger} = onClickOutside(myRef, handler, {controls: true});
 * ```
 */
export function onClickOutside<T extends OnClickOutsideOptions>(target: MaybeElementRef, handler: OnClickOutsideHandler<T>, options?: T): () => void;
export function onClickOutside<T extends OnClickOutsideOptions<true>>(
    target: MaybeElementRef,
    handler: OnClickOutsideHandler<T>,
    options: T,
): OnClickOutsideControlsReturn;
export function onClickOutside<T extends OnClickOutsideOptions<boolean>>(
    target: MaybeElementRef,
    handler: OnClickOutsideHandler<T>,
    options?: T,
): (() => void) | OnClickOutsideControlsReturn {
    return vueUseOnClickOutside(
        target,
        handler as OnClickOutsideHandler<OnClickOutsideOptions<true>>,
        {
            ...options,
            ignore: () => [...toValue(options?.ignore ?? []), ...IGNORED_WHITELIST],
        } as OnClickOutsideOptions<true>,
    );
}
